import { sql } from "drizzle-orm";
import { pgTable, text, varchar, integer, boolean, timestamp, jsonb, real, index, uniqueIndex, unique, check } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

import { tenants } from './common';

export const claimFieldTypeSchema = z.enum([
  "text",
  "number",
  "date",
  "boolean",
  "select",
  "file",
  "image",
]);
export type ClaimFieldType = z.infer<typeof claimFieldTypeSchema>;
export const selectOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
});
export const numberConfigSchema = z.object({
  min: z.number().optional(),
  max: z.number().optional(),
  precision: z.number().int().min(0).optional(),
  unit: z.string().optional(),
});
export const selectConfigSchema = z.object({
  options: z.array(selectOptionSchema).optional(),
});
export const textConfigSchema = z.object({
  charLimit: z.number().int().positive().optional(),
  multiline: z.boolean().optional(),
  pattern: z.string().optional(),
});
export const dateConfigSchema = z.object({
  constraint: z.enum(["future", "past", "none"]).optional(),
});
export const booleanConfigSchema = z.object({
  trueLabel: z.string().optional(),
  falseLabel: z.string().optional(),
});
export const fileConfigSchema = z.object({
  acceptedFormats: z.array(z.string()).optional(),
  maxSizeMb: z.number().positive().optional(),
});
export const imageConfigSchema = z.object({
  acceptedFormats: z.array(z.string()).optional(),
  maxSizeMb: z.number().positive().optional(),
  aspectRatio: z.string().optional(),
});

const baseClaimFieldSchema = z.object({
  key: z.string(),
  label: z.string(),
  required: z.boolean().default(false),
  placeholder: z.string().optional(),
  helpText: z.string().optional(),
  defaultValue: z.union([z.string(), z.number(), z.boolean()]).optional(),
  visibility: z.string().optional(),
  privacyClass: z.string().optional(),
  validationRules: z.record(z.any()).optional(),
});
export const claimFieldSchema = z.discriminatedUnion("dataType", [
  baseClaimFieldSchema.extend({ dataType: z.literal("text"), typeConfig: textConfigSchema.optional() }),
  baseClaimFieldSchema.extend({ dataType: z.literal("number"), typeConfig: numberConfigSchema.optional() }),
  baseClaimFieldSchema.extend({ dataType: z.literal("date"), typeConfig: dateConfigSchema.optional() }),
  baseClaimFieldSchema.extend({ dataType: z.literal("boolean"), typeConfig: booleanConfigSchema.optional() }),
  baseClaimFieldSchema.extend({ dataType: z.literal("select"), typeConfig: selectConfigSchema.optional() }),
  baseClaimFieldSchema.extend({ dataType: z.literal("file"), typeConfig: fileConfigSchema.optional() }),
  baseClaimFieldSchema.extend({ dataType: z.literal("image"), typeConfig: imageConfigSchema.optional() }),
]);
export type ClaimField = z.infer<typeof claimFieldSchema>;
export const claimSchemaSchema = z.object({
  claims: z.array(claimFieldSchema),
});
export type ClaimSchema = z.infer<typeof claimSchemaSchema>;
export const credentialTemplates = pgTable("credential_templates", {
  id: varchar("id").primaryKey(),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  name: text("name").notNull(),
  version: text("version").notNull(),
  description: text("description").notNull(),
  category: text("category").notNull(),
  scope: text("scope").notNull(),
  linkedAreas: text("linked_areas").array().default([]),
  linkedPolicies: text("linked_policies").array().default([]),
  requiredForRoles: text("required_for_roles").array().default([]),
  documentType: text("document_type").notNull(),
  validationFields: text("validation_fields").array().default([]),
  expiryType: text("expiry_type").notNull(),
  validityDurationMonths: integer("validity_duration_months"),
  renewalRequired: boolean("renewal_required").notNull().default(true),
  autoSuspendAccessOnExpiry: boolean("auto_suspend_access_on_expiry").notNull().default(false),
  approvalRequired: boolean("approval_required").notNull().default(false),
  status: text("status").notNull().default("draft"),
  createdBy: text("created_by"),
  effectiveDate: text("effective_date"),
  createdAt: timestamp("created_at", { withTimezone: true }).notNull().defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  usedByCount: integer("used_by_count").notNull().default(0),
  applicableSites: text("applicable_sites").array().default([]),
  applicableAreas: text("applicable_areas").array().default([]),
  complianceChecklist: jsonb("compliance_checklist").$type<{
    id: string;
    title: string;
    type: string;
    mandatory: boolean;
    description?: string;
  }[]>().default([]),

  code: text("code"),
  tenantScope: text("tenant_scope"),
  regionScope: text("region_scope"),
  siteScope: text("site_scope").array().default([]),
  industryScope: text("industry_scope"),
  versionNumber: integer("version_number").default(1),
  externallyGoverned: boolean("externally_governed").notNull().default(false),

  subtype: text("subtype"),
  domain: text("domain"),
  riskRelevant: boolean("risk_relevant").notNull().default(false),
  ptwRelevant: boolean("ptw_relevant").notNull().default(false),
  siteAccessRelevant: boolean("site_access_relevant").notNull().default(false),

  subjectType: text("subject_type").default("worker"),
  holderModel: text("holder_model").default("individual"),

  claimSchema: jsonb("claim_schema").$type<ClaimSchema>().default({ claims: [] }),

  requiredEvidenceTypes: text("required_evidence_types").array().default([]),
  manualReviewRequired: boolean("manual_review_required").notNull().default(false),
  autoIssuanceAllowed: boolean("auto_issuance_allowed").notNull().default(false),

  validityPeriodDays: integer("validity_period_days"),
  renewable: boolean("renewable").notNull().default(true),
  recertificationRequired: boolean("recertification_required").notNull().default(false),
  suspensionAllowed: boolean("suspension_allowed").notNull().default(true),
  revocationAllowed: boolean("revocation_allowed").notNull().default(true),
  gracePeriodDays: integer("grace_period_days").default(0),
  reminderSchedule: jsonb("reminder_schedule").$type<number[]>().default([]),

  approvedIssuerTypes: text("approved_issuer_types").array().default([]),
  verificationPolicy: text("verification_policy"),
  shareable: boolean("shareable").notNull().default(false),
  qrEnabled: boolean("qr_enabled").notNull().default(false),
  selectiveDisclosure: boolean("selective_disclosure").notNull().default(false),

  cardFormat: text("card_format").default("standard"),
  cardColor: text("card_color"),
  cardIcon: text("card_icon"),
  displayPriority: integer("display_priority").default(0),
  badgeText: text("badge_text"),
  languageSupport: text("language_support").array().default([]),

  translations: jsonb("translations").$type<{
    [lang: string]: {
      name?: string;
      description?: string;
      claimLabels?: { [claimKey: string]: string };
    };
  }>().default({}),

  sourceGlobalTemplateId: text("source_global_template_id"),
  sourceGlobalTemplateVersion: text("source_global_template_version"),
  isGlobalCopy: boolean("is_global_copy").notNull().default(false),

  requiredForDepartments: text("required_for_departments").array().default([]),

  requiredDocumentTypes: jsonb("required_document_types").$type<{
    evidenceLabel: string;
    documentTaxonomyType?: string;
    documentTaxonomyCategory?: string;
    required: boolean;
  }[]>().default([]),

  acknowledgementRequired: boolean("acknowledgement_required").notNull().default(false),
  linkedAcknowledgementDocumentType: text("linked_acknowledgement_document_type"),
  issuedOnOnboardingComplete: boolean("issued_on_onboarding_complete").notNull().default(false),
}, (t) => ({
  idxCredentialTemplatesTenantStatus: index("idx_credential_templates_tenant_status").on(t.tenantId, t.status),
  // Task #1920 — DB-level CHECK keeping status confined to the union of values
  // the credential engine actually accepts. Mirrors migration 0132.
  credentialTemplatesStatusCheck: check(
    "credential_templates_status_check",
    sql`${t.status} IN ('draft', 'active', 'published', 'retired', 'archived')`,
  ),
}));
export const insertCredentialTemplateSchema = createInsertSchema(credentialTemplates).omit({ tenantId: true, createdAt: true });
export type InsertCredentialTemplate = z.infer<typeof insertCredentialTemplateSchema>;
export type CredentialTemplate = typeof credentialTemplates.$inferSelect;

// ─── Global Credential Templates (Marketplace) ───────────────────────
export const globalCredentialTemplates = pgTable("global_credential_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  version: text("version").notNull().default("1.0"),
  description: text("description").notNull().default(""),
  category: text("category").notNull(),
  industryTags: text("industry_tags").array().default([]),
  status: text("status").notNull().default("draft"),
  claimSchema: jsonb("claim_schema").$type<ClaimSchema>().default({ claims: [] }),
  translations: jsonb("translations").$type<{
    [lang: string]: {
      name?: string;
      description?: string;
      claimLabels?: { [claimKey: string]: string };
    };
  }>().default({}),
  cardColor: text("card_color").default("#1e40af"),
  cardIcon: text("card_icon").default("shield"),
  scope: text("scope").default("Global"),
  documentType: text("document_type").default("certificate"),
  expiryType: text("expiry_type").default("date"),
  validityPeriodDays: integer("validity_period_days").default(365),
  usageCount: integer("usage_count").notNull().default(0),
  versionHistory: jsonb("version_history").$type<{ version: string; updatedAt: string; snapshot: Record<string, any> }[]>().default([]),
  publishedBy: text("published_by"),
  publishedAt: text("published_at"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  deprecatedAt: text("deprecated_at"),
  deprecatedBy: text("deprecated_by"),
}, (t) => ({
  // Task #1920 — DB-level CHECK keeping status confined to the marketplace
  // editorial workflow vocabulary. Mirrors migration 0132.
  globalCredentialTemplatesStatusCheck: check(
    "global_credential_templates_status_check",
    sql`${t.status} IN ('draft', 'published', 'deprecated', 'archived')`,
  ),
}));
export const insertGlobalCredentialTemplateSchema = createInsertSchema(globalCredentialTemplates).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertGlobalCredentialTemplate = z.infer<typeof insertGlobalCredentialTemplateSchema>;
export type GlobalCredentialTemplate = typeof globalCredentialTemplates.$inferSelect;

// ─── Compliance Frameworks ────────────────────────────────────────────
export const complianceFrameworks = pgTable("compliance_frameworks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  code: text("code").notNull().unique(),
  description: text("description").default(""),
  region: text("region"),
  industry: text("industry"),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertComplianceFrameworkSchema = createInsertSchema(complianceFrameworks).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertComplianceFramework = z.infer<typeof insertComplianceFrameworkSchema>;
export type ComplianceFramework = typeof complianceFrameworks.$inferSelect;

// ─── Global Template Compliance Framework Tags ────────────────────────
export const globalTemplateComplianceTags = pgTable("global_template_compliance_tags", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  globalTemplateId: text("global_template_id").notNull().references(() => globalCredentialTemplates.id),
  complianceFrameworkId: text("compliance_framework_id").notNull().references(() => complianceFrameworks.id),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertGlobalTemplateComplianceTagSchema = createInsertSchema(globalTemplateComplianceTags).omit({ id: true, createdAt: true });
export type InsertGlobalTemplateComplianceTag = z.infer<typeof insertGlobalTemplateComplianceTagSchema>;
export type GlobalTemplateComplianceTag = typeof globalTemplateComplianceTags.$inferSelect;

// ─── Template Bundles ─────────────────────────────────────────────────
export const templateBundles = pgTable("template_bundles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  description: text("description").default(""),
  industry: text("industry"),
  useCaseTag: text("use_case_tag"),
  templateIds: text("template_ids").array().default([]),
  status: text("status").notNull().default("active"),
  isFeatured: boolean("is_featured").notNull().default(false),
  requiredTier: text("required_tier"),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertTemplateBundleSchema = createInsertSchema(templateBundles).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertTemplateBundle = z.infer<typeof insertTemplateBundleSchema>;
export type TemplateBundle = typeof templateBundles.$inferSelect;

// ─── Global Template Editorial Workflow States ────────────────────────
export const globalTemplateReviews = pgTable("global_template_reviews", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  globalTemplateId: text("global_template_id").notNull().references(() => globalCredentialTemplates.id),
  workflowState: text("workflow_state").notNull().default("draft"),
  reviewedBy: text("reviewed_by"),
  reviewedAt: timestamp("reviewed_at", { withTimezone: true }),
  comment: text("comment"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertGlobalTemplateReviewSchema = createInsertSchema(globalTemplateReviews).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertGlobalTemplateReview = z.infer<typeof insertGlobalTemplateReviewSchema>;
export type GlobalTemplateReview = typeof globalTemplateReviews.$inferSelect;

// ─── Template Flags (tenant-submitted issues) ─────────────────────────
export const templateFlags = pgTable("template_flags", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  globalTemplateId: text("global_template_id").notNull().references(() => globalCredentialTemplates.id),
  tenantId: text("tenant_id").references(() => tenants.id),
  flagType: text("flag_type").notNull().default("inaccuracy"),
  description: text("description").notNull(),
  status: text("status").notNull().default("open"),
  resolvedBy: text("resolved_by"),
  resolvedAt: timestamp("resolved_at", { withTimezone: true }),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertTemplateFlagSchema = createInsertSchema(templateFlags).omit({ id: true, createdAt: true });
export type InsertTemplateFlag = z.infer<typeof insertTemplateFlagSchema>;
export type TemplateFlag = typeof templateFlags.$inferSelect;

// ─── Global Verified Issuers Registry ────────────────────────────────
export const globalVerifiedIssuers = pgTable("global_verified_issuers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  issuerCode: text("issuer_code"),
  description: text("description").default(""),
  contactEmail: text("contact_email"),
  contactPhone: text("contact_phone"),
  website: text("website"),
  logoUrl: text("logo_url"),
  accreditationDetails: text("accreditation_details"),
  geographicScope: text("geographic_scope").array().default([]),
  authorizedTemplateIds: text("authorized_template_ids").array().default([]),
  trustLevel: text("trust_level").notNull().default("Self-Declared"),
  verificationStatus: text("verification_status").notNull().default("pending"),
  verifiedBy: text("verified_by"),
  verifiedAt: timestamp("verified_at", { withTimezone: true }),
  applicationNotes: text("application_notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertGlobalVerifiedIssuerSchema = createInsertSchema(globalVerifiedIssuers).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertGlobalVerifiedIssuer = z.infer<typeof insertGlobalVerifiedIssuerSchema>;
export type GlobalVerifiedIssuer = typeof globalVerifiedIssuers.$inferSelect;

// ─── Credential Template Versions ────────────────────────────────────
export const credentialTemplateVersions = pgTable("credential_template_versions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  templateId: text("template_id").notNull(),
  versionNumber: integer("version_number").notNull(),
  claimSchema: jsonb("claim_schema").$type<Record<string, any>>().default({}),
  lifecycleConfig: jsonb("lifecycle_config").$type<Record<string, any>>().default({}),
  rules: jsonb("rules").$type<Record<string, any>>().default({}),
  translations: jsonb("translations").$type<Record<string, any>>(),
  immutable: boolean("immutable").notNull().default(true),
  publishedAt: text("published_at"),
  publishedBy: text("published_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertCredentialTemplateVersionSchema = createInsertSchema(credentialTemplateVersions).omit({ id: true, tenantId: true });
export type InsertCredentialTemplateVersion = z.infer<typeof insertCredentialTemplateVersionSchema>;
export type CredentialTemplateVersion = typeof credentialTemplateVersions.$inferSelect;

// ─── Issuers ─────────────────────────────────────────────────────────
export const issuers = pgTable("issuers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  code: text("code").notNull(),
  name: text("name").notNull(),
  issuerType: text("issuer_type").notNull().default("organization"),
  legalEntity: text("legal_entity"),
  displayName: text("display_name"),
  logo: text("logo"),

  tenantScope: text("tenant_scope"),
  siteScope: text("site_scope").array().default([]),
  regionScope: text("region_scope"),
  workDomainScope: text("work_domain_scope").array().default([]),
  credentialTemplateScope: text("credential_template_scope").array().default([]),
  subjectScope: text("subject_scope").array().default([]),

  verified: boolean("verified").notNull().default(false),
  trustLevel: text("trust_level").notNull().default("standard"),
  approvedBy: text("approved_by"),
  trustSource: text("trust_source"),
  complianceReviewed: boolean("compliance_reviewed").notNull().default(false),
  externalRegistryRef: text("external_registry_ref"),

  mayIssueManually: boolean("may_issue_manually").notNull().default(true),
  mayIssueAutomatically: boolean("may_issue_automatically").notNull().default(false),
  requiresDualApproval: boolean("requires_dual_approval").notNull().default(false),
  requiresEvidenceReview: boolean("requires_evidence_review").notNull().default(true),
  mayRevoke: boolean("may_revoke").notNull().default(true),
  maySuspend: boolean("may_suspend").notNull().default(true),
  mayRenew: boolean("may_renew").notNull().default(true),
  mayBulkIssue: boolean("may_bulk_issue").notNull().default(false),
  maxIssuanceScope: text("max_issuance_scope"),

  externalSystemRef: text("external_system_ref"),
  syncRules: jsonb("sync_rules").$type<Record<string, any>>().default({}),

  status: text("status").notNull().default("active"),
  apiIssuer: boolean("api_issuer").notNull().default(false),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  uniqIssuersTenantCode: uniqueIndex("uniq_issuers_tenant_code").on(t.tenantId, t.code),
}));
export const insertIssuerSchema = createInsertSchema(issuers).omit({ id: true, tenantId: true }).extend({
  name: z.string({ required_error: "Name is required" }).trim().min(1, "Name is required"),
  code: z.string({ required_error: "Code is required" }).trim().min(1, "Code is required"),
});
export type InsertIssuer = z.infer<typeof insertIssuerSchema>;
export type Issuer = typeof issuers.$inferSelect;

// ─── Issuer–Template Mappings ────────────────────────────────────────
// Task #1920 — Tenant isolation hardening:
//   • FK issuerId  → issuers(id)              ON DELETE CASCADE
//   • FK templateId → credential_templates(id) ON DELETE CASCADE
//   • UNIQUE (tenant_id, issuer_id, template_id)
// The DB also enforces, via migration 0132's pre-flight scrub, that the
// referenced issuer/template share the row's tenant_id so cross-tenant
// linkage cannot be persisted.
export const issuerTemplateMappings = pgTable("issuer_template_mappings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  issuerId: text("issuer_id").notNull().references(() => issuers.id, { onDelete: "cascade" }),
  templateId: text("template_id").notNull().references(() => credentialTemplates.id, { onDelete: "cascade" }),
  allowedSubjectScope: text("allowed_subject_scope").array().default([]),
  allowedSiteScope: text("allowed_site_scope").array().default([]),
  issuanceMode: text("issuance_mode").default("manual"),
  approvalModel: text("approval_model").default("single"),
  validityOverrideDays: integer("validity_override_days"),
  evidenceOverrideRules: jsonb("evidence_override_rules").$type<Record<string, any>>().default({}),
  active: boolean("active").notNull().default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  uniqIssuerTemplateMappingsTenantIssuerTemplate: uniqueIndex(
    "uniq_issuer_template_mappings_tenant_issuer_template",
  ).on(t.tenantId, t.issuerId, t.templateId),
}));
export const insertIssuerTemplateMappingSchema = createInsertSchema(issuerTemplateMappings).omit({ id: true, tenantId: true });
export type InsertIssuerTemplateMapping = z.infer<typeof insertIssuerTemplateMappingSchema>;
export type IssuerTemplateMapping = typeof issuerTemplateMappings.$inferSelect;

// ─── Issuer Invitations ───────────────────────────────────────────────
export const issuerInvitations = pgTable("issuer_invitations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  invitedByUserId: text("invited_by_user_id").notNull(),
  organizationName: text("organization_name").notNull(),
  contactEmail: text("contact_email").notNull(),
  contactName: text("contact_name"),
  issuerId: text("issuer_id"),
  invitationToken: text("invitation_token").notNull().unique(),
  status: text("status").notNull().default("pending"),
  authorizedTemplateIds: text("authorized_template_ids").array().default([]),
  initialTrustLevel: text("initial_trust_level").notNull().default("provisional"),
  message: text("message"),
  expiresAt: timestamp("expires_at", { withTimezone: true }),
  acceptedAt: timestamp("accepted_at", { withTimezone: true }),
  acceptedByUserId: text("accepted_by_user_id"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertIssuerInvitationSchema = createInsertSchema(issuerInvitations).omit({ id: true, tenantId: true });
export type InsertIssuerInvitation = z.infer<typeof insertIssuerInvitationSchema>;
export type IssuerInvitation = typeof issuerInvitations.$inferSelect;

// ─── Issuer Governance Settings ───────────────────────────────────────
export const issuerGovernanceSettings = pgTable("issuer_governance_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").references(() => tenants.id),
  isPlatformDefault: boolean("is_platform_default").notNull().default(false),
  provisionalApproverRole: text("provisional_approver_role").notNull().default("Tenant Admin"),
  verifiedApproverRole: text("verified_approver_role").notNull().default("Credential Manager"),
  accreditedRequiresApproval: boolean("accredited_requires_approval").notNull().default(false),
  maxApprovalDays: integer("max_approval_days").notNull().default(7),
  minEvidenceForVerified: integer("min_evidence_for_verified").notNull().default(5),
  minEvidenceForAccredited: integer("min_evidence_for_accredited").notNull().default(20),
  trustUpgradeRequiresSuperAdminReview: boolean("trust_upgrade_requires_super_admin_review").notNull().default(false),
  platformMinProvisionalApprover: text("platform_min_provisional_approver"),
  platformMinVerifiedApprover: text("platform_min_verified_approver"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertIssuerGovernanceSettingsSchema = createInsertSchema(issuerGovernanceSettings).omit({ id: true });
export type InsertIssuerGovernanceSettings = z.infer<typeof insertIssuerGovernanceSettingsSchema>;
export type IssuerGovernanceSettings = typeof issuerGovernanceSettings.$inferSelect;

// ─── Issuer Trust Upgrade Requests ────────────────────────────────────
export const issuerTrustUpgradeRequests = pgTable("issuer_trust_upgrade_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  issuerId: text("issuer_id").notNull(),
  requestedByUserId: text("requested_by_user_id").notNull(),
  fromTrustLevel: text("from_trust_level").notNull(),
  toTrustLevel: text("to_trust_level").notNull(),
  status: text("status").notNull().default("pending"),
  evidenceSummary: text("evidence_summary"),
  issuanceCount: integer("issuance_count").default(0),
  approvalRate: real("approval_rate").default(0),
  revocationRate: real("revocation_rate").default(0),
  reviewedByUserId: text("reviewed_by_user_id"),
  reviewedAt: timestamp("reviewed_at", { withTimezone: true }),
  reviewNotes: text("review_notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertIssuerTrustUpgradeRequestSchema = createInsertSchema(issuerTrustUpgradeRequests).omit({ id: true, tenantId: true });
export type InsertIssuerTrustUpgradeRequest = z.infer<typeof insertIssuerTrustUpgradeRequestSchema>;
export type IssuerTrustUpgradeRequest = typeof issuerTrustUpgradeRequests.$inferSelect;

// ─── Wallets ─────────────────────────────────────────────────────────
export const wallets = pgTable("wallets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  walletCode: text("wallet_code"),
  walletType: text("wallet_type").notNull().default("custodial"),
  holderWorkerId: text("holder_worker_id"),
  holderType: text("holder_type").default("worker"),
  walletStatus: text("wallet_status").notNull().default("active"),

  createdByUser: text("created_by_user"),
  activationDate: text("activation_date"),
  onboardingStatus: text("onboarding_status").default("pending"),
  recoveryStatus: text("recovery_status").default("none"),
  deviceBinding: text("device_binding"),
  accessMethod: text("access_method").default("pin"),

  authMethod: text("auth_method").default("pin"),
  pinEnabled: boolean("pin_enabled").notNull().default(false),
  biometricEnabled: boolean("biometric_enabled").notNull().default(false),
  recoveryMethod: text("recovery_method"),
  keyMaterialRef: text("key_material_ref"),
  deviceTrust: text("device_trust"),
  lastAccessDate: text("last_access_date"),

  totalCredentials: integer("total_credentials").default(0),
  activeCredentials: integer("active_credentials").default(0),
  expiredCredentials: integer("expired_credentials").default(0),
  revokedCredentials: integer("revoked_credentials").default(0),
  suspendedCredentials: integer("suspended_credentials").default(0),
  pendingCredentials: integer("pending_credentials").default(0),
  sharedPresentations: integer("shared_presentations").default(0),
  verificationCount: integer("verification_count").default(0),

  uxSummary: jsonb("ux_summary").$type<Record<string, any>>().default({}),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  uqWalletWorkerTenant: uniqueIndex("uq_wallet_worker_tenant").on(t.holderWorkerId, t.tenantId),
}));
export const insertWalletSchema = createInsertSchema(wallets).omit({ id: true, tenantId: true });
export type InsertWallet = z.infer<typeof insertWalletSchema>;
export type Wallet = typeof wallets.$inferSelect;

// ─── Credentials (issued instances) ──────────────────────────────────
export const credentials = pgTable("credentials", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  credentialNumber: text("credential_number").notNull(),
  templateId: text("template_id").notNull(),
  templateVersionId: text("template_version_id"),
  issuerId: text("issuer_id"),
  walletId: text("wallet_id"),
  workerId: text("worker_id").notNull(),
  subjectId: text("subject_id"),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  siteScope: text("site_scope").array().default([]),

  claimsPayload: jsonb("claims_payload").$type<Record<string, any>>().default({}),
  normalizedClaims: jsonb("normalized_claims").$type<Record<string, any>>().default({}),
  renderedSummary: text("rendered_summary"),
  privacyMetadata: jsonb("privacy_metadata").$type<Record<string, any>>().default({}),

  lifecycleStatus: text("lifecycle_status").notNull().default("draft"),
  issueDate: text("issue_date"),
  effectiveDate: text("effective_date"),
  expiryDate: text("expiry_date"),
  suspendedDate: text("suspended_date"),
  revokedDate: text("revoked_date"),
  archivedDate: text("archived_date"),
  renewalDueDate: text("renewal_due_date"),

  issuanceBasis: text("issuance_basis"),
  evidenceRefs: text("evidence_refs").array().default([]),
  sourceWorkflowRef: text("source_workflow_ref"),
  trainingResultRef: text("training_result_ref"),
  approvalRef: text("approval_ref"),
  imported: boolean("imported").notNull().default(false),
  externalFlag: boolean("external_flag").notNull().default(false),
  externalCredentialId: text("external_credential_id"),

  shareableFlag: boolean("shareable_flag").notNull().default(false),
  verificationMethod: text("verification_method"),
  lastPresentedDate: text("last_presented_date"),
  lastVerifiedDate: text("last_verified_date"),
  presentationCount: integer("presentation_count").default(0),

  createdByUser: text("created_by_user"),
  issuedByUser: text("issued_by_user"),
  approvedByUser: text("approved_by_user"),
  revokedByUser: text("revoked_by_user"),
  suspensionReason: text("suspension_reason"),
  revocationReason: text("revocation_reason"),
  versionedHistory: jsonb("versioned_history").$type<{ version: number; date: string; changedBy?: string }[]>().default([]),

  workerAcknowledgedAt: timestamp("worker_acknowledged_at", { withTimezone: true }),

  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxCredentialsTenantId: index("idx_credentials_tenant_id").on(t.tenantId),
  idxCredentialsExpiryDate: index("idx_credentials_expiry_date").on(t.expiryDate),
  idxCredentialsTenantWorkerId: index("idx_credentials_tenant_worker_id").on(t.tenantId, t.workerId),
  idxCredentialsTenantLifecycleStatus: index("idx_credentials_tenant_lifecycle_status").on(t.tenantId, t.lifecycleStatus),
  idxCredentialsTenantTemplatLifecycle: index("idx_credentials_tenant_template_lifecycle").on(t.tenantId, t.templateId, t.lifecycleStatus),
  idxCredentialsTenantExpiryDate: index("idx_credentials_tenant_expiry_date").on(t.tenantId, t.expiryDate),
  idxCredentialsWorkerTenantStatus: index("idx_credentials_worker_tenant_status").on(t.workerId, t.tenantId, t.lifecycleStatus),
  // Task #1921 — Race-free guard against duplicate live credentials for the
  // same (tenant, worker, template). The pre-flight check in
  // `validateIssuanceAndCreate` is a fast path; this partial unique index
  // is the authoritative DB-level constraint that closes the issuance race
  // window. Mirrors `migrations/0132_credentials_unique_active_per_template.sql`.
  uqCredentialsActivePerTemplate: uniqueIndex("credentials_unique_active_per_template_idx")
    .on(t.tenantId, t.workerId, t.templateId)
    .where(sql`lifecycle_status IN ('active', 'issued', 'pending', 'pending_evidence')`),
}));
export const insertCredentialSchema = createInsertSchema(credentials).omit({ id: true, tenantId: true });
export type InsertCredential = z.infer<typeof insertCredentialSchema>;
export type Credential = typeof credentials.$inferSelect;

// ─── Credential Evidence ─────────────────────────────────────────────
export const credentialEvidence = pgTable("credential_evidence", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  credentialId: text("credential_id").notNull(),
  evidenceType: text("evidence_type").notNull(),
  sourceReference: text("source_reference"),
  fileRef: text("file_ref"),
  fileName: text("file_name"),
  mimeType: text("mime_type"),
  fileSize: integer("file_size"),
  documentRef: text("document_ref"),
  uploadedBy: text("uploaded_by"),
  reviewedBy: text("reviewed_by"),
  reviewStatus: text("review_status").default("pending"),
  reviewDate: text("review_date"),
  notes: text("notes"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertCredentialEvidenceSchema = createInsertSchema(credentialEvidence).omit({ id: true, tenantId: true });
export type InsertCredentialEvidence = z.infer<typeof insertCredentialEvidenceSchema>;
export type CredentialEvidence = typeof credentialEvidence.$inferSelect;

// ─── Credential Audit Logs ──────────────────────────────────────────
export const credentialAuditLogs = pgTable("credential_audit_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  eventType: text("event_type").notNull(),
  actor: text("actor"),
  actorRole: text("actor_role"),
  timestamp: text("timestamp").notNull(),
  oldState: text("old_state"),
  newState: text("new_state"),
  affectedEntityType: text("affected_entity_type"),
  affectedEntityId: text("affected_entity_id"),
  evidence: text("evidence"),
  comments: text("comments"),
  sourceDevice: text("source_device"),
  sourceSession: text("source_session"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertCredentialAuditLogSchema = createInsertSchema(credentialAuditLogs).omit({ id: true, tenantId: true });
export type InsertCredentialAuditLog = z.infer<typeof insertCredentialAuditLogSchema>;
export type CredentialAuditLog = typeof credentialAuditLogs.$inferSelect;

// ─── Credential View Logs (Access Audit Trail) ───────────────────────
export const credentialViewLogs = pgTable("credential_view_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  credentialId: text("credential_id").notNull(),
  workerId: text("worker_id").notNull(),
  viewerId: text("viewer_id").notNull(),
  viewerName: text("viewer_name"),
  viewerRole: text("viewer_role").notNull(),
  viewContext: text("view_context").notNull().default("verification"),
  ipAddress: text("ip_address"),
  userAgent: text("user_agent"),
  flagged: boolean("flagged").notNull().default(false),
  flagReason: text("flag_reason"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxCredentialViewLogsTenantCreatedAt: index("idx_credential_view_logs_tenant_created_at").on(t.tenantId, t.createdAt),
  idxCredentialViewLogsCredentialCreatedAt: index("idx_credential_view_logs_credential_created_at").on(t.credentialId, t.createdAt),
}));
export const insertCredentialViewLogSchema = createInsertSchema(credentialViewLogs).omit({ id: true, tenantId: true, createdAt: true });
export type InsertCredentialViewLog = z.infer<typeof insertCredentialViewLogSchema>;
export type CredentialViewLog = typeof credentialViewLogs.$inferSelect;

// ─── Audit Retention Policies ────────────────────────────────────────
export const auditRetentionPolicies = pgTable("audit_retention_policies", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  retentionDays: integer("retention_days").notNull().default(365),
  minRetentionDays: integer("min_retention_days").notNull().default(90),
  maxRetentionDays: integer("max_retention_days").notNull().default(2555),
  legalHold: boolean("legal_hold").notNull().default(false),
  legalHoldReason: text("legal_hold_reason"),
  updatedByUserId: text("updated_by_user_id"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertAuditRetentionPolicySchema = createInsertSchema(auditRetentionPolicies).omit({ id: true, tenantId: true, createdAt: true });
export type InsertAuditRetentionPolicy = z.infer<typeof insertAuditRetentionPolicySchema>;
export type AuditRetentionPolicy = typeof auditRetentionPolicies.$inferSelect;

// ─── Pack 10.C — Tenant Retention Overrides (Task #1023) ─────────────────
// Per-tenant overrides for the generalized retention engine
// (server/services/retention-engine.ts). The engine ships with a static
// registry of policy classes (e.g. "audit_events", "user_sessions",
// "password_reset_tokens", "notifications", "credential_view_logs"); each
// row in this table overrides the default retentionDays for one
// (tenantId, policyKey) pair. Rows can also assert a per-policy legalHold
// that suppresses sweeps even when the platform-wide hold is off.
//
// Why a separate table from audit_retention_policies?
//   - audit_retention_policies is single-row-per-tenant and only governs the
//     audit / credential view logs cluster (Pack 5 / Pack 6).
//   - This table is multi-row-per-tenant (one row per policy key) and
//     governs every retention-eligible table the engine knows about. That
//     keeps the audit-specific table stable while letting commercial /
//     compliance teams sell longer retention for, e.g., notifications without
//     touching audit retention.
export const tenantRetentionOverrides = pgTable("tenant_retention_overrides", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id, { onDelete: "cascade" }),
  // Stable identifier of the retention policy class (e.g. "audit_events",
  // "user_sessions"). Validated against the registry in retention-engine.ts.
  policyKey: text("policy_key").notNull(),
  retentionDays: integer("retention_days").notNull(),
  legalHold: boolean("legal_hold").notNull().default(false),
  reason: text("reason"),
  updatedByUserId: text("updated_by_user_id"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  uniqTenantPolicy: unique("uniq_tenant_retention_overrides_tenant_policy").on(t.tenantId, t.policyKey),
  idxTenantPolicy: index("idx_tenant_retention_overrides_tenant_policy").on(t.tenantId, t.policyKey),
}));
export const insertTenantRetentionOverrideSchema = createInsertSchema(tenantRetentionOverrides).omit({ id: true, createdAt: true, updatedAt: true });
export type InsertTenantRetentionOverride = z.infer<typeof insertTenantRetentionOverrideSchema>;
export type TenantRetentionOverride = typeof tenantRetentionOverrides.$inferSelect;

// ─── Audit Suspicious Access Flags ──────────────────────────────────
export const auditSuspiciousFlags = pgTable("audit_suspicious_flags", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  flagType: text("flag_type").notNull(),
  description: text("description").notNull(),
  severity: text("severity").notNull().default("medium"),
  relatedViewLogId: text("related_view_log_id"),
  relatedViewerId: text("related_viewer_id"),
  relatedWorkerId: text("related_worker_id"),
  investigationStatus: text("investigation_status").notNull().default("open"),
  resolvedByUserId: text("resolved_by_user_id"),
  resolvedAt: timestamp("resolved_at", { withTimezone: true }),
  resolutionNotes: text("resolution_notes"),
  detectedAt: timestamp("detected_at", { withTimezone: true }).defaultNow(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertAuditSuspiciousFlagSchema = createInsertSchema(auditSuspiciousFlags).omit({ id: true, tenantId: true, createdAt: true });
export type InsertAuditSuspiciousFlag = z.infer<typeof insertAuditSuspiciousFlagSchema>;
export type AuditSuspiciousFlag = typeof auditSuspiciousFlags.$inferSelect;

// ─── Credential Requests ────────────────────────────────────────────
export const credentialRequests = pgTable("credential_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  requestType: text("request_type").notNull(),
  requesterId: text("requester_id"),
  requesterType: text("requester_type"),
  templateId: text("template_id").notNull(),
  subjectWorkerId: text("subject_worker_id").notNull(),
  status: text("status").notNull().default("requested"),
  evidenceRefs: text("evidence_refs").array().default([]),
  reviewerId: text("reviewer_id"),
  reviewNotes: text("review_notes"),
  requestedDate: text("requested_date"),
  reviewedDate: text("reviewed_date"),
  issuedDate: text("issued_date"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertCredentialRequestSchema = createInsertSchema(credentialRequests).omit({ id: true, tenantId: true });
export type InsertCredentialRequest = z.infer<typeof insertCredentialRequestSchema>;
export type CredentialRequest = typeof credentialRequests.$inferSelect;

// ─── Credential Bundles ─────────────────────────────────────────────
export const credentialBundles = pgTable("credential_bundles", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  name: text("name").notNull(),
  code: text("code").notNull(),
  bundleType: text("bundle_type").notNull(),
  description: text("description"),
  requiredTemplateIds: text("required_template_ids").array().default([]),
  siteScope: text("site_scope").array().default([]),
  linkedAccessPolicy: text("linked_access_policy"),
  linkedPtwPolicy: text("linked_ptw_policy"),
  status: text("status").notNull().default("active"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertCredentialBundleSchema = createInsertSchema(credentialBundles).omit({ id: true, tenantId: true });
export type InsertCredentialBundle = z.infer<typeof insertCredentialBundleSchema>;
export type CredentialBundle = typeof credentialBundles.$inferSelect;

// ─── Bulk Operation Logs ─────────────────────────────────────────────
export const bulkOperationLogs = pgTable("bulk_operation_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  operationType: text("operation_type").notNull(), // 'bulk_issue', 'bulk_revoke', 'batch_validate', 'kiosk_validate'
  actor: text("actor").notNull(),
  actorRole: text("actor_role").notNull(),
  templateId: text("template_id"),
  issuerId: text("issuer_id"),
  filterCriteria: jsonb("filter_criteria").$type<Record<string, any>>(),
  affectedCount: integer("affected_count").notNull().default(0),
  successCount: integer("success_count").notNull().default(0),
  failureCount: integer("failure_count").notNull().default(0),
  details: jsonb("details").$type<Record<string, any>>(),
  reason: text("reason"),
  timestamp: text("timestamp").notNull(),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertBulkOperationLogSchema = createInsertSchema(bulkOperationLogs).omit({ id: true, tenantId: true });
export type InsertBulkOperationLog = z.infer<typeof insertBulkOperationLogSchema>;
export type BulkOperationLog = typeof bulkOperationLogs.$inferSelect;

// ─── Credential Bundle Rules ────────────────────────────────────────
export const credentialBundleRules = pgTable("credential_bundle_rules", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  bundleId: text("bundle_id").notNull(),
  templateId: text("template_id").notNull(),
  requiredIssuerTrustLevel: text("required_issuer_trust_level"),
  notExpired: boolean("not_expired").notNull().default(true),
  notSuspended: boolean("not_suspended").notNull().default(true),
  siteMatch: boolean("site_match").notNull().default(false),
  customRule: jsonb("custom_rule").$type<Record<string, any>>().default({}),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertCredentialBundleRuleSchema = createInsertSchema(credentialBundleRules).omit({ id: true, tenantId: true });
export type InsertCredentialBundleRule = z.infer<typeof insertCredentialBundleRuleSchema>;
export type CredentialBundleRule = typeof credentialBundleRules.$inferSelect;

// ─── Credential Dependencies ─────────────────────────────────────────
// Defines template-level prerequisite relationships scoped within a tenant.
// cascadePolicy: "auto_revoke" = automatically revoke dependent on revocation of prerequisite;
//                "flag_for_review" = mark dependent as flagged but don't auto-revoke.
export const credentialDependencies = pgTable("credential_dependencies", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  prerequisiteTemplateId: text("prerequisite_template_id").notNull(),
  dependentTemplateId: text("dependent_template_id").notNull(),
  cascadePolicy: text("cascade_policy").notNull().default("flag_for_review"),
  description: text("description"),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertCredentialDependencySchema = createInsertSchema(credentialDependencies).omit({ id: true, tenantId: true });
export type InsertCredentialDependency = z.infer<typeof insertCredentialDependencySchema>;
export type CredentialDependency = typeof credentialDependencies.$inferSelect;

// ─── Credential Revocation Appeals ───────────────────────────────────
// State machine: submitted → under_review → approved | rejected
export const credentialRevocationAppeals = pgTable("credential_revocation_appeals", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  credentialId: text("credential_id").notNull(),
  appellantId: text("appellant_id").notNull(),
  appellantRole: text("appellant_role"),
  reason: text("reason").notNull(),
  evidenceRefs: text("evidence_refs").array().default([]),
  status: text("status").notNull().default("submitted"),
  reviewerId: text("reviewer_id"),
  reviewerRole: text("reviewer_role"),
  reviewNotes: text("review_notes"),
  reviewedAt: text("reviewed_at"),
  submittedAt: text("submitted_at").notNull(),
  appealWindowExpiresAt: text("appeal_window_expires_at"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertCredentialRevocationAppealSchema = createInsertSchema(credentialRevocationAppeals).omit({ id: true, tenantId: true });
export type InsertCredentialRevocationAppeal = z.infer<typeof insertCredentialRevocationAppealSchema>;
export type CredentialRevocationAppeal = typeof credentialRevocationAppeals.$inferSelect;

// ─── Revocation Governance Settings ──────────────────────────────────
// Platform-wide settings managed by Super Admin.
// scope: "platform" for global defaults; future: "tenant" for overrides.
export const revocationGovernanceSettings = pgTable("revocation_governance_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  scope: text("scope").notNull().default("platform"),
  tenantId: text("tenant_id"),
  minimumRevokeRole: text("minimum_revoke_role").notNull().default("Credential Manager"),
  massRevocationRole: text("mass_revocation_role").notNull().default("Tenant Admin"),
  mandatoryReasonRequired: boolean("mandatory_reason_required").notNull().default(true),
  appealWindowDays: integer("appeal_window_days").notNull().default(14),
  allowedReasonCategories: text("allowed_reason_categories").array().default(["policy_violation", "contract_end", "credential_fraud", "safety_concern", "data_error", "other"]),
  defaultCascadePolicy: text("default_cascade_policy").notNull().default("flag_for_review"),
  scheduledRevocationEnabled: boolean("scheduled_revocation_enabled").notNull().default(true),
  scheduledRevocationCronExpr: text("scheduled_revocation_cron_expr").default("0 2 * * *"),
  templateGovernanceOverrides: jsonb("template_governance_overrides").$type<{
    [templateId: string]: {
      minimumRevokeRole?: string;
      defaultCascadePolicy?: string;
    };
  }>().default({}),
  updatedBy: text("updated_by"),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertRevocationGovernanceSettingsSchema = createInsertSchema(revocationGovernanceSettings).omit({ id: true });
export type InsertRevocationGovernanceSettings = z.infer<typeof insertRevocationGovernanceSettingsSchema>;
export type RevocationGovernanceSettings = typeof revocationGovernanceSettings.$inferSelect;
export const credentialAlerts = pgTable("credential_alerts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  workerId: text("worker_id").notNull(),
  workerName: text("worker_name"),
  credentialId: text("credential_id").notNull(),
  credentialType: text("credential_type").notNull(),
  expiryDate: text("expiry_date").notNull(),
  hoursUntilExpiry: integer("hours_until_expiry").notNull(),
  alertType: text("alert_type").notNull().default("credential_expiry"),
  status: text("status").notNull().default("pending"),
  notifiedAt: text("notified_at"),
  acknowledgedAt: text("acknowledged_at"),
  acknowledgedBy: text("acknowledged_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  // Pack 6 / 6.A.1 — schema-forward index: dedupe credential-expiry dispatch lookups
  // by tenant + alert_type + recency window (proactive-alert-engine + fanout audit).
  byTenantAlertNotifiedAt: index("credential_alerts_tenant_alert_notified_at_idx").on(t.tenantId, t.alertType, t.notifiedAt),
}));
export const insertCredentialAlertSchema = createInsertSchema(credentialAlerts).omit({ id: true, createdAt: true });
export type InsertCredentialAlert = z.infer<typeof insertCredentialAlertSchema>;
export type CredentialAlert = typeof credentialAlerts.$inferSelect;

// ─── Credential Acknowledgements ─────────────────────────────────────
// Records a worker's signed acknowledgement of receipt for a credential
// where the template has acknowledgementRequired = true.
export const credentialAcknowledgements = pgTable("credential_acknowledgements", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  credentialId: text("credential_id").notNull(),
  workerId: text("worker_id").notNull(),
  acknowledgedAt: timestamp("acknowledged_at", { withTimezone: true }).notNull().defaultNow(),
  method: text("method").notNull().default("checkbox"),
  signatureData: text("signature_data"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertCredentialAcknowledgementSchema = createInsertSchema(credentialAcknowledgements).omit({ id: true, tenantId: true, createdAt: true });
export type InsertCredentialAcknowledgement = z.infer<typeof insertCredentialAcknowledgementSchema>;
export type CredentialAcknowledgement = typeof credentialAcknowledgements.$inferSelect;

// ─── Participation Role Types ─────────────────────────────────────────
